import React, {ComponentProps, useEffect, type JSX} from 'react';
import {
  cancelAnimationFrame,
  requestAnimationFrame,
} from 'react-viewport-utils';
import {useResetForced404Page} from './context';
import {
  getScrollTopAnchorId,
  shouldForceActionPush,
  useHistory,
} from './useHistory';
// eslint-disable-next-line monorepo/no-internal-import
import {isElementFullyInViewport} from '@aboutyou/utils/src/isElementFullyInViewport';

export const DEFAULT_SCROLL_ANCHOR_ID = 'aboutyou_router__SCROLL_ANCHOR';

// Resets focus to the top of the document, same as a hard page load, so the
// next Tab press reveals the skip links first instead of continuing from
// wherever focus happened to be (e.g. a persistent header link that isn't
// remounted across routes). Only called for full page-type navigations (see
// caller) — scroll-anchored in-page navigations (pagination, filters)
// deliberately leave focus untouched, since resetting it there would break
// keyboard users mid-pagination. Filter and sort updates carry no scroll
// anchor, so they are recognised by their history state instead, and so is
// anything that leaves the URL unchanged, such as closing an overlay.
//
// `blur()` alone is not enough: it clears `document.activeElement` but
// browsers keep their internal sequential-focus-navigation cursor at the
// blurred element's old position, so the next Tab continues from there
// instead of from the top of the document. An explicit `focus()` (here, on
// `document.body` via a temporary tabindex) is what actually resets that
// cursor — same logic as `@aboutyou/a11y`'s `resetFocusToRoot`, duplicated
// here rather than imported since `@aboutyou/a11y` transitively depends on
// this package (via `@aboutyou/data-fetcher`), so importing it back would be
// a circular dependency.
const resetFocusToBody = () => {
  const hadTabIndex = document.body.hasAttribute('tabindex');
  if (!hadTabIndex) {
    document.body.setAttribute('tabindex', '-1');
    document.body.addEventListener(
      'blur',
      () => document.body.removeAttribute('tabindex'),
      {once: true},
    );
  }
  document.body.focus();
};

const isFilterOrSortUpdate = (state: unknown) => {
  const {isFiltering, isSorting} = (state ?? {}) as Record<string, unknown>;
  return Boolean(isFiltering || isSorting);
};

/**
 * Listens to events on the history and improves the scroll position based on them.
 */
export const HistoryEventHandler = () => {
  const history = useHistory();
  const resetForced404Page = useResetForced404Page();

  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    let lastTickId: number | undefined;
    let lastLocation = history.location;
    const removeListener = history.listen(
      (location, action) => {
        // UI updates (an overlay opening) are not observed here, so closing
        // one lands on the URL that was last handled
        const keepFocus =
          isFilterOrSortUpdate(location.state) ||
          (location.pathname === lastLocation.pathname &&
            location.search === lastLocation.search);
        lastLocation = location;

        const handleNavigation = () => {
          lastTickId = undefined;
          const anchorId = getScrollTopAnchorId(history.location);
          const scrollAnchor = anchorId
            ? document.getElementById(anchorId)
            : null;

          // Unlike focus, scroll position is restored natively by the
          // browser on POP, so only adjust it ourselves for PUSH (and
          // forced REPLACE) — doing it on POP too would fight that native
          // restoration.
          const isRealNavigation =
            action === 'PUSH' ||
            (action === 'REPLACE' && shouldForceActionPush(location)); // treat REPLACE as PUSH when forced

          if (!scrollAnchor) {
            if (!keepFocus) {
              resetFocusToBody();
            }
            if (isRealNavigation) {
              window.scrollTo(0, 0);
            }
            return;
          }

          if (isRealNavigation && !isElementFullyInViewport(scrollAnchor)) {
            const defaultOffsetTop =
              Number(scrollAnchor.dataset?.defaultOffsetTop) ?? 0;
            window.scrollTo({
              top: scrollAnchor.offsetTop - defaultOffsetTop,
            });
          }
        };

        if (lastTickId) {
          cancelAnimationFrame(lastTickId);
        }
        lastTickId = requestAnimationFrame(handleNavigation);
        resetForced404Page();
      },
      {
        disableUiUpdates: true,
      },
    );
    return () => {
      if (lastTickId) {
        cancelAnimationFrame(lastTickId);
      }
      removeListener();
    };
  }, [history, resetForced404Page]);
  return null;
};

interface ScrollTopAnchorProps<
  C extends keyof JSX.IntrinsicElements | React.ComponentType<any>,
> {
  anchorRef?: React.RefObject<HTMLElement | null>;
  as: C;
}

/**
 * Can be placed somewhere on the page. If it exists, and a link with property `considerScrollTopAnchor`, the page will jump to this element instead of the top of the page.
 */
export const ScrollTopAnchor = <
  C extends keyof JSX.IntrinsicElements | React.ComponentType<any>,
>({
  anchorId,
  anchorRef,
  as: Component = 'div',
  defaultOffsetTop = 0,
  ...props
}: ScrollTopAnchorProps<C> &
  ComponentProps<C> & {defaultOffsetTop?: number}) => {
  return (
    <Component
      id={anchorId ?? DEFAULT_SCROLL_ANCHOR_ID}
      data-default-offset-top={defaultOffsetTop}
      ref={anchorRef}
      {...props}
    />
  );
};
